import React, { useEffect, useId, useMemo } from 'react';
import { Tooltip } from '../common/tooltip.component';
import { useTranslation } from 'react-i18next';
import { BankMethodValue, BriteCustomerContext, BankFieldErrors } from '../../features/bank/bank.types';
import { BANK_NAME, REVOLUT_REFERRAL_URL } from '../../features/bank/bank.constants';
import { buildBriteSessionPayload, sanitizeClearingNumber, sanitizeAccountNumber } from '../../features/bank/bank.helpers';
import { useBriteContext } from '../../features/bank/brite-widget-host';
import { BriteSurface, readBriteReturnIntent } from '../../features/bank/brite-resume';
import './bank-method-selector.style.scss';
import BriteImg from '../../../../public/assets/brite.jpeg'
export interface BankMethodSelectorProps {
  value: BankMethodValue;
  onChange: (value: BankMethodValue) => void;
  errors?: BankFieldErrors;
  briteContext: BriteCustomerContext;
  onBriteCompleted: () => void;
  onBriteError?: (message: string) => void;
  showRevolutMarketing?: boolean;
  disabled?: boolean;
  className?: string;
  // Set false for surfaces where Brite can't run yet (e.g. pre-authentication
  // signup steps) — the Brite row isn't rendered at all, only International/Manual.
  allowBrite?: boolean;
  briteSurface: BriteSurface;
}

const BriteMarkIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3 4 6.5v5.2c0 4.4 3.2 8 8 9.3 4.8-1.3 8-4.9 8-9.3V6.5L12 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="m9 12 2.2 2.2L15.5 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const GlobeIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 12h17M12 3.5c2.2 2.3 3.4 5.2 3.4 8.5s-1.2 6.2-3.4 8.5c-2.2-2.3-3.4-5.2-3.4-8.5S9.8 5.8 12 3.5Z" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const PencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BuildingIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 21V9.5L12 4l8 5.5V21" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="M9 21v-6h6v6M4 21h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const BoltIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M13 3 5 13.5h5.5L10 21l8-10.5h-5.5L13 3Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="currentColor" />
    <path d="m8.5 12.2 2.4 2.4 4.6-5.2" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ShieldIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 3.5 5 6v5.7c0 4.3 3 7.7 7 9 4-1.3 7-4.7 7-9V6l-7-2.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    <path d="m9.2 12 2.1 2.1 3.7-4.2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path d="M12 11v5.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="8.1" r="0.9" fill="currentColor" />
  </svg>
);

export const BankMethodSelector: React.FC<BankMethodSelectorProps> = ({
  value,
  onChange,
  errors,
  briteContext,
  onBriteCompleted,
  onBriteError,
  showRevolutMarketing = true,
  disabled = false,
  className = '',
  allowBrite = true,
  briteSurface,
}) => {
  const { t, i18n } = useTranslation();
  const widgetId = useId().replace(/[:]/g, '');
  const radioGroupName = `bank-method-${widgetId}`;
const language = new URLSearchParams(window.location.search).get('lang');

  const { isCreatingSession, isWidgetActive, startBriteFlow, resumeBriteFlow, finalizeBriteFlow } = useBriteContext();
  const briteCallbacks = { onCompleted: onBriteCompleted, onError: onBriteError };

  useEffect(() => {
    if (!allowBrite) return;
    const intent = readBriteReturnIntent(briteSurface);
    if (!intent) return;
    if (intent.status === 'resume') {
      resumeBriteFlow(intent.flow, briteCallbacks);
    } else {
      finalizeBriteFlow(intent.flow, briteCallbacks);
    }
  }, []);
  const effectiveMethod = !allowBrite && value.method === 'brite' ? 'other-swedish' : value.method;

  const internationalMethod = useMemo(
    () => (value.method === 'revolut' ? 'revolut' : 'foreign'),
    [value.method]
  );

  const isBriteChecked = allowBrite && effectiveMethod === 'brite';
  const isInternationalChecked = effectiveMethod === 'foreign' || effectiveMethod === 'revolut';
  const isManualChecked = effectiveMethod === 'other-swedish';

  const handleConnectBrite = () => {
    if (value.method !== 'brite') {
      onChange({ ...value, group: 'national', method: 'brite' });
    }
    const payload = buildBriteSessionPayload(briteContext, i18n.language || 'en-us', briteSurface);
    startBriteFlow(payload, briteSurface, briteCallbacks);
  };

  const switchToManual = () =>
    onChange({ ...value, group: 'national', method: 'other-swedish', bank: value.otherBankName || '' });

  const selectInternational = (method: 'foreign' | 'revolut') =>
    onChange({
      ...value,
      group: 'international',
      method,
      bank: method === 'revolut' ? BANK_NAME.REVOLUT : BANK_NAME.FOREIGN,
    });

  return (
    <div className={`bank-method-selector ${className}`}>
      <div className="bank-method-selector__options" role="radiogroup" aria-label={t('BANK_CONNECTION_METHOD') || 'Bank connection method'}>
        {allowBrite && (
          <div className={`bank-method-selector__row ${isBriteChecked ? 'is-checked' : ''}`}>
            <label className="bank-method-selector__row-header">
              <input
                type="radio"
                name={radioGroupName}
                disabled={disabled}
                checked={isBriteChecked}
                // onClick (not onChange) so re-clicking while already selected still
                // reopens the Brite modal — onChange only fires on a checked-state change.
                onChange={() => {}}
                onClick={handleConnectBrite}
              />
              <span className="bank-method-selector__row-icon">

                <img src={BriteImg} width="60" />

              </span>
              <span className="bank-method-selector__row-title-group">
                <span className="bank-method-selector__row-title">
                  {t('BANK_METHOD_BRITE_TITLE') || 'Retrieve your bank details automatically'}
                  <Tooltip
                    content={t('BRITE_INFO_TOOLTIP') || 'This option is currently available for Swedish banks only and requires identification with Mobile BankID.'}
                    position="top"
                  >
                    <span
                      className="bank-method-selector__row-info"
                      tabIndex={0}
                      role="button"
                      aria-label={t('BRITE_INFO_TOOLTIP') || 'This option is currently available for Swedish banks only and requires identification with Mobile BankID.'}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <InfoIcon />
                    </span>
                  </Tooltip>
                  <span className="bank-method-selector__row-badge">{t('RECOMMENDED') || 'Recommended'}</span>
                </span>
                <span className="bank-method-selector__row-subtitle">{t('BRITE_BENEFIT_COPY') || 'Securely link your bank in seconds — verified instantly, no manual entry.'}</span>
              </span>
            </label>

            {isBriteChecked && (
              <div className="bank-method-selector__row-body">
                {(value.bbanNumber || value.ibanNumber) && (
                  <div className="bank-method-selector__brite-linked">
                    <span className="bank-method-selector__brite-linked-label">
                      {value.bbanNumber
                        ? (t('ACCOUNT_INFORMATION') || 'BBAN Number')
                        : (t('IBAN_NUMBER') || 'IBAN Number')}
                    </span>
                    <span className="bank-method-selector__brite-linked-value">
                      {`******${(value.bbanNumber || value.ibanNumber || '').slice(-4)}`}
                    </span>
                  </div>
                )}

                <p className="bank-method-selector__row-copy">
                  {t('BRITE_BENEFIT_COPY') || 'Securely link your bank in seconds — verified instantly, no manual entry.'}
                  {' '}
                </p>

                <div className="bank-method-selector__brite-why">
                  <span className="bank-method-selector__brite-why-title">{t('BRITE_WHY_TITLE') || 'Why connect with Brite?'}</span>
                  <ul className="bank-method-selector__brite-why-list">
                    <li>
                      <span className="bank-method-selector__brite-why-icon"><ShieldIcon /></span>
                      {t('BRITE_BENEFIT_SECURITY') || 'Bank-grade security — your login details are never stored or shared with us.'}
                    </li>
                    <li>
                      <span className="bank-method-selector__brite-why-icon"><BoltIcon /></span>
                      {t('BRITE_BENEFIT_INSTANT') || "Instant verification — your account is confirmed directly with your bank, so there's nothing to type wrong."}
                    </li>
                    <li>
                      <span className="bank-method-selector__brite-why-icon"><ClockIcon /></span>
                      {t('BRITE_BENEFIT_SPEED') || 'Faster payouts — verified details mean no delays waiting for manual review.'}
                    </li>
                  </ul>
                </div>

                <div className="bank-method-selector__brite-actions">
                  <button
                    type="button"
                    className="bank-method-selector__btn bank-method-selector__btn--primary"
                    disabled={disabled || isCreatingSession || isWidgetActive}
                    onClick={handleConnectBrite}
                  >
                    {isCreatingSession ? (
                      <>
                        <span className="bank-method-selector__spinner" aria-hidden="true" />
                        {t('BRITE_CONNECTING') || 'Connecting…'}
                      </>
                    ) : (
                      <>{t('CONNECT_WITH_BRITE') || 'Retrieve your bank details automatically'}</>
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="bank-method-selector__brite-notice" role="note">
              <span className="bank-method-selector__brite-notice-icon"><ClockIcon /></span>
              <span>
                <strong>{t('PLEASE_NOTE') || 'Please note:'}</strong>
                {' '}
                {t('BRITE_VERIFICATION_DELAY_NOTICE') || 'In some cases, verifying your bank details may take some time. Please wait while we complete the verification process.'}
              </span>
            </div>
          </div>
        )}

        <div className={`bank-method-selector__row ${isInternationalChecked ? 'is-checked' : ''}`}>
          <label className="bank-method-selector__row-header">
            <input
              type="radio"
              name={radioGroupName}
              disabled={disabled}
              checked={isInternationalChecked}
              onChange={() => selectInternational(internationalMethod)}
            />
            <span className="bank-method-selector__row-icon bank-method-selector__row-icon--tile bank-method-selector__row-icon--international"><GlobeIcon /></span>
            <span className="bank-method-selector__row-title-group">
              <span className="bank-method-selector__row-title">{t('BANK_METHOD_INTERNATIONAL_TITLE') || 'International payment'}</span>
              <span className="bank-method-selector__row-subtitle">{t('BANK_METHOD_INTERNATIONAL_SUBTITLE') || 'Enter BIC and IBAN for Revolut or a foreign bank.'}</span>
            </span>
          </label>

          {isInternationalChecked && (
            <div className="bank-method-selector__row-body">
              <div className="bank-method-selector__option-cards">
                <button
                  type="button"
                  disabled={disabled}
                  className={`bank-method-selector__option-card ${internationalMethod === 'foreign' ? 'is-active' : ''}`}
                  onClick={() => selectInternational('foreign')}
                >
                  {internationalMethod === 'foreign' && <span className="bank-method-selector__option-check"><CheckCircleIcon /></span>}
                  <span className="bank-method-selector__option-icon"><BuildingIcon /></span>
                  <span className="bank-method-selector__option-text">
                    <strong>{t('FOREIGN_BANK') || 'Foreign bank'}</strong>
                    <small>BIC / IBAN</small>
                  </span>
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  className={`bank-method-selector__option-card ${internationalMethod === 'revolut' ? 'is-active' : ''}`}
                  onClick={() => selectInternational('revolut')}
                >
                  {internationalMethod === 'revolut' && <span className="bank-method-selector__option-check"><CheckCircleIcon /></span>}
                  <span className="bank-method-selector__option-icon bank-method-selector__option-icon--revolut"><BoltIcon /></span>
                  <span className="bank-method-selector__option-text">
                    <strong>{t('REVOLUT_BANK') || 'Revolut'}</strong>
                    <small>{t('REVOLUT_INSTANT') || 'Instant transfer'}</small>
                  </span>
                </button>
              </div>

              <div className="bank-method-selector__field-row">
                <div className="bank-method-selector__field">
                  <label>{t('BIC_NUMBER') || 'BIC Number'}</label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={value.bicNumber || ''}
                    onChange={(e) => onChange({ ...value, group: 'international', method: internationalMethod, bicNumber: e.target.value })}
                    placeholder={t('TYPE_HERE') || 'Type here....'}
                  />
                  {errors?.bicNumber && <span className="bank-method-selector__error">{errors.bicNumber}</span>}
                </div>
                <div className="bank-method-selector__field">
                  <label>{t('IBAN_NUMBER') || 'IBAN Number'}</label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={value.ibanNumber || ''}
                    onChange={(e) => onChange({ ...value, group: 'international', method: internationalMethod, ibanNumber: e.target.value })}
                    placeholder={t('TYPE_HERE') || 'Type here....'}
                  />
                  {errors?.ibanNumber && <span className="bank-method-selector__error">{errors.ibanNumber}</span>}
                </div>
              </div>

              {/* {showRevolutMarketing && (
                <div className="bank-method-selector__revolut-banner">
                  <h5>{t('GET_PAID_WITHIN_HOUR_WITH_REVOLUT') || 'Get paid within an hour with Revolut.'}</h5>
                  <p>
                    {t('REVOLUT_ACCOUNT_DESCRIPTION') || "If you already have a Revolut account, simply update your bank details in the platform and we'll pay your salary into your Revolut account. Don't have one?"}
                    {' '}
                    <a href={REVOLUT_REFERRAL_URL} target="_blank" rel="noopener noreferrer">
                      {t('SIGN_UP_HERE') || 'Sign up here'}
                    </a>
                    {' '}
                    {t('AND_START_BENEFITING_TODAY') || 'and start benefiting today.'}
                  </p>
                </div>
              )} */}
            </div>
          )}
        </div>

        <div className={`bank-method-selector__row ${isManualChecked ? 'is-checked' : ''}`}>
          <label className="bank-method-selector__row-header">
            <input
              type="radio"
              name={radioGroupName}
              disabled={disabled}
              checked={isManualChecked}
              onChange={switchToManual}
            />
            <span className="bank-method-selector__row-icon bank-method-selector__row-icon--tile"><PencilIcon /></span>
            <span className="bank-method-selector__row-title-group">
              <span className="bank-method-selector__row-title">{t('BANK_METHOD_MANUAL_TITLE') || 'Enter bank details manually'}</span>
              <span className="bank-method-selector__row-subtitle">{t('BANK_METHOD_MANUAL_SUBTITLE') || 'Add your bank name, clearing and account number yourself.'}</span>
            </span>
          </label>

          {isManualChecked && (
            <div className="bank-method-selector__row-body">
              <div className="bank-method-selector__field">
                <label>{t('BANK_NAME_CUSTOM') || 'Bank Name'}</label>
                <input
                  type="text"
                  disabled={disabled}
                  value={value.otherBankName || ''}
                  onChange={(e) => onChange({ ...value, group: 'national', method: 'other-swedish', otherBankName: e.target.value, bank: e.target.value })}
                  placeholder={t('TYPE_HERE') || 'Type here....'}
                />
                {errors?.otherBankName && <span className="bank-method-selector__error">{errors.otherBankName}</span>}
              </div>

              <div className="bank-method-selector__field-row">
                <div className="bank-method-selector__field">
                  <label>{t('CLEARING_NUMBER') || 'Clearing Number'}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    disabled={disabled}
                    value={value.clearingNumber || ''}
                    onChange={(e) => onChange({ ...value, group: 'national', method: 'other-swedish', clearingNumber: sanitizeClearingNumber(e.target.value) })}
                    placeholder={t('TYPE_HERE') || 'Type here....'}
                  />
                  {errors?.clearingNumber && <span className="bank-method-selector__error">{errors.clearingNumber}</span>}
                </div>

                <div className="bank-method-selector__field">
                  <label>{t('ACCOUNT_NUMBER') || 'Account Number'}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    disabled={disabled}
                    value={value.accountNumber || ''}
                    onChange={(e) => onChange({ ...value, group: 'national', method: 'other-swedish', accountNumber: sanitizeAccountNumber(e.target.value) })}
                    placeholder={t('TYPE_HERE') || 'Type here....'}
                  />
                  {errors?.accountNumber && <span className="bank-method-selector__error">{errors.accountNumber}</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BankMethodSelector;
